import { Inject, Injectable, OnInit } from "@angular/core";
import { ApiService } from "../api/api.service";
import { BehaviorSubject, Observable, tap } from "rxjs";
import { LoginCredentialsDto, SmsDto } from "../../models/auth/auth.model";
import { HttpClient } from "@angular/common/http";
import { Router } from "@angular/router";
import jwt_decode from "jwt-decode";
import { UserInfoService } from "../user-info.service";
import { ToastrService } from "ngx-toastr";

@Injectable({
	providedIn: "root",
})
export class AuthService {
	private token: string | null = null;
	private isAuth: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	timer: any = undefined;

	constructor(
		private apiService: ApiService,
		private http: HttpClient,
		private router: Router,
		private toastrService: ToastrService,
		private userInfoService: UserInfoService,
		@Inject("BACKEND_API_URL") private apiUrl: string
	) {
		this.refreshTokenInterval();
		this.initStatus();
	}

	initStatus() {
		if (!!localStorage.getItem("auth-token")) {
			this.isAuth.next(true);
		}
	}

	logout() {
		if (this.getToken()) {
			this.apiService.httpGet<void>("/auth/logout").subscribe();
		}

		this.clearLocalStorage();
		this.timer = undefined;
		this.token = null;
		this.isAuth.next(false);
		this.router.navigate(["/login"]);
	}

	login(body: LoginCredentialsDto): Observable<{ token: string; is_first_auth: boolean }> {
		return this.http.post<{ token: string; is_first_auth: boolean }>(this.apiUrl + "/auth/login", body).pipe(
			tap(({ token }) => {
				const parseToken: any = jwt_decode(token);
				const agentId = parseToken.agent_id;

				if (parseToken.is_two_factor_auth) {
					this.router.navigate(["/sms-code"]);
					localStorage.setItem("time", "60");
					localStorage.setItem("auth-token", token);
				} else {
					if (!!agentId) {
						this.finalAuth(token);
					} else {
						let errorTitle = localStorage.getItem("locale") === "ru" ? "Ошибка!" : "Қате!";
						let errorDesc =
							localStorage.getItem("locale") === "ru"
								? `Пользователь с почтой ${body.login} не найден`
								: `Пайдаланушының ${body.login} поштасы табылмады.`;

						this.toastrService.warning(errorDesc, errorTitle);
					}
				}
			})
		);
	}

	loginFromSms(data: SmsDto): Observable<{ token: string }> {
		const url = `/auth/confirm_otp`;
		return this.apiService.httpPost<{ token: string }>(url, data);
	}

	resendSmsCode() {
		const url = `/auth/refresh_otp`;
		return this.apiService.httpGet<string>(url);
	}

	finalAuth(token: string) {
		this.isAuth.next(true);
		this.setToken(token);
		if (!this.timer) {
			this.refreshTokenInterval();
		}
		this.router.navigate(["/payment-list"]);
	}

	refreshTokenInterval() {
		this.timer = setInterval(() => {
			if (this.isAuthenticated()) {
				this.refreshToken();
			} else {
				clearInterval(this.timer);
			}
		}, 600000);
	}

	refreshToken() {
		if (this.getToken()) {
			const parseToken: any = jwt_decode(this.getToken() ?? "");
			const expData = parseToken.access_token_exp;
			const currentDate = Date.now();
			const expDataToken = new Date(expData - 1200000);
			const updateTime = new Date(currentDate);

			if (updateTime >= expDataToken) {
				this.apiService.httpGet<{ token: string }>(`/auth/refresh_token`).subscribe({
					next: (res) => {
						this.setToken(res.token);
					},
					error: () => {
						this.logout();
					},
				});
			}
		} else {
			this.logout();
		}
	}

	setToken(token: string) {
		this.token = token;
		this.setLocalStorage(token);
	}

	getToken(): string | null {
		return localStorage.getItem("auth-token");
	}

	getAgentId(): string | null {
		return localStorage.getItem("auth-agent_id");
	}

	getUserId(): string | null {
		return localStorage.getItem("agent_id");
	}

	getUserPermissions(): string[] {
		const token: any = jwt_decode(this.getToken() || "");
		return token.authorities.map((obj: any) => obj.authority) || [];
	}

	isAuthenticated(): boolean {
		return !!localStorage.getItem("auth-token");
	}

	setLocalStorage(token: string) {
		const parseToken: any = jwt_decode(token);
		const userId = parseToken.user_id;
		const agentId = parseToken.agent_id;
		const authorities = parseToken.authorities;

		localStorage.setItem("auth-token", token);
		localStorage.setItem("agent_id", agentId);
		localStorage.setItem("user_id", userId);

		this.userInfoService.userId$.next(userId);
		this.userInfoService.agentId$.next(agentId);
	}

	clearLocalStorage() {
		localStorage.removeItem("auth-token");
		localStorage.removeItem("agent_id");
		localStorage.removeItem("user_id");
	}

	get isAuth$() {
		return this.isAuth.asObservable();
	}
}
